/**
 * QR food labels (owner, 2026-10-11, after FreshMise: "QR batch labels; scan with an iPad camera; three choices Dispose / Open /
 * Move; live expiry dashboard"). Every printed food label carries a small QR code; scanning it opens THAT food with three big
 * buttons. Pure. This file: the token and the address (Q1-Q3). foodscan.ts: what each button may do now (Q5-Q9) and the "Soonest
 * to expire" list. Every date, every "throw it out" and every refusal stays the food engine's own (foodstore.ts).
 *
 * | Rule | Why |
 * |---|---|
 * | Q1 The code holds an address and nothing else: https://app.cleancleaver.com/f/<token>. No food name, no business, no date | A label sits in a shared walk-in, on a delivery cart, in a trash bag; anyone can read a QR |
 * | Q2 The token is 128 random bits (22 base64url characters) from a CSPRNG, made once per food and never reused | Unguessable: 2^128 tries; a scan proves only that you hold the label |
 * | Q3 A token opens the food only for a signed-in member of that business who may use its location (the server checks every time); anyone else gets the same "not one of yours" answer whether the token exists or not | The QR is a pointer, not a key: the record stays private, and nobody can probe which tokens are real |
 * | Q4 The human-readable date stays the main thing on the label; the QR is small, on the side, and the label still prints without one | The Food Code date mark (3-501.17) is what an inspector and a cook read; the code is a shortcut |
 * | Q5 Three buttons, always shown: Threw it out · Opened it · Moved it. One that can't be done now says why on the button (never hidden, never a wall) | One look, one tap; the reason is the lesson |
 * | Q6 Threw it out asks why in one tap (Past its date · Out too long · Spoiled · Something else), the engine's own reason picked already; the throw-out is never refused | Never block a discard (Food Code 3-501.18, 3-701.11) |
 * | Q7 Opened it runs the engine's "opened" act (the shorter after-opening clock, never a later date: F2, F4) and is offered only while it is sealed | Opening twice would rewrite the opened day |
 * | Q8 Moved it: another kind of place is that place's own act (into the fridge, into the freezer, out of the fridge, a fridge thaw), so the F-rules run; the same kind of place is "moved" (another spot, nothing else changes) | A move never restarts a clock |
 * | Q9 A food that must go (F10 throwOutFlag) offers Threw it out; Opened it and Moved it say "Throw it out: …" | Safety first, in plain words |
 * | Q10 Every act is a food act row (who, when, how; the PIN's person on a shared tablet) and is in the binder; Undo inside 30 s voids it (kept, not counted) | It is the record |
 */
// The three buttons, where food can move and Soonest to expire are in foodscan.ts, imported by its path (not from the core index), so
// the web loads them with the screens that use them (the demo's main bundle budget, apps/web/test/bundle-budget.ts).

/** Where a label's QR sends a phone camera. Overridable per server (WEB_ORIGIN, the same as the sheets' QR) so staging prints staging links. */
export const LABEL_QR_ORIGIN = "https://app.cleancleaver.com";
/** Q2: 16 random bytes, as 22 base64url characters. */
export const LABEL_TOKEN_BYTES = 16;
const TOKEN = /^[A-Za-z0-9_-]{22}$/;
export const isLabelToken = (s: unknown): s is string => typeof s === "string" && TOKEN.test(s);

const B64URL = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789-_";
/** 16 bytes → 22 base64url characters (no padding). */
export function tokenFromBytes(bytes: Uint8Array): string {
  if (bytes.length !== LABEL_TOKEN_BYTES) throw new Error(`A label token is ${LABEL_TOKEN_BYTES} bytes.`);
  let out = "";
  for (let i = 0; i < 16; i += 3) {
    const a = bytes[i]!, b = bytes[i + 1], c = bytes[i + 2];
    const n = (a << 16) | ((b ?? 0) << 8) | (c ?? 0);
    out += B64URL[(n >> 18) & 63]! + B64URL[(n >> 12) & 63]!;
    if (b !== undefined) out += B64URL[(n >> 6) & 63]!;
    if (c !== undefined) out += B64URL[n & 63]!;
  }
  return out;
}

/** Q2: a fresh token from the platform's CSPRNG (Web Crypto getRandomValues: browsers, Bun, and expo-crypto's polyfill on the phone). */
export function newLabelToken(random: (b: Uint8Array) => Uint8Array = (b) => globalThis.crypto.getRandomValues(b)): string {
  return tokenFromBytes(random(new Uint8Array(LABEL_TOKEN_BYTES)));
}

/** Q1: the address in the QR. */
export function labelQrUrl(token: string, origin: string = LABEL_QR_ORIGIN): string {
  if (!isLabelToken(token)) throw new Error("Not a label token.");
  return `${origin.replace(/\/+$/, "")}/f/${token}`;
}

/** The token in a scanned code, or null for any other QR (menus, Wi-Fi, a printed sheet). Any host: staging labels scan on staging. */
export function parseLabelQr(text: string): string | null {
  const m = text.trim().match(/^https?:\/\/[^/?#\s]+(?:\/app)?\/f\/([A-Za-z0-9_-]{22})\/?(?:[?#][^\s]*)?$/);
  return m ? m[1]! : null;
}

